<template>
  <WdsModal
    :show="internalModelValue"
    :title="$t('Confirm acceptance of the document')"
    :z-index="1100"
    class="accept-contract-doc-modal"
    @cancel="internalModelValue = false"
  >
    <template #default>
      <div class="tw-w-full tw-overflow-hidden tw-h-[calc(100vh-350px)] tw-max-h-[600px]">
        <iframe
          :src="contractDoc.pdfUrl"
          class="tw-w-full tw-h-full tw-border-0"
          title="Contract document PDF"
        />
      </div>
      <WdsStack align-center class="tw-mt-2">
        <WdsText sm>{{ $t('Having trouble viewing?') }}</WdsText>
        <WdsButtonGhost secondary xs :href="contractDoc.pdfUrl" target="_blank">
          {{ $t('Download document') }}
        </WdsButtonGhost>
      </WdsStack>
    </template>

    <template #footer>
      <div class="tw-flex tw-justify-between">
        <WdsButton @click="internalModelValue = false">
          {{ $t('Cancel') }}
        </WdsButton>

        <WdsButton
          primary
          data-test="confirm-kid-button"
          :disabled="accepting"
          @click="confirmAcceptance"
        >
          {{ accepting ? $t('Confirming...') : $t('Confirm acceptance') }}
        </WdsButton>
      </div>
    </template>
  </WdsModal>
</template>

<script setup>
import { WdsButton, WdsButtonGhost, WdsModal, WdsStack, WdsText } from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { useToast } from '@/composables/useToast'
import { useTranslation } from '@/composables/translation'

const { $t } = useTranslation()
const toast = useToast()

const props = defineProps({
  contractDoc: {
    type: Object,
    required: true,
  },
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'accepted'])

const accepting = ref(false)

const formAction = computed(() =>
  laravelNamedRoute(namedRoutes['contract-doc'].accept, {
    contractDoc: props.contractDoc.localId,
  }),
)

const internalModelValue = computed({
  get() {
    return props.modelValue
  },
  set(modelValue) {
    emit('update:modelValue', modelValue)
  },
})

const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? null

const confirmAcceptance = async () => {
  const csrfToken = getCsrfToken()
  if (!csrfToken) {
    toast.error($t('Unable to submit. Please refresh and try again.'))
    return
  }

  accepting.value = true

  try {
    const response = await fetch(formAction.value, {
      method: 'POST',
      headers: {
        'X-CSRF-TOKEN': csrfToken,
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      credentials: 'same-origin',
      body: new URLSearchParams(),
    })

    if (!response.ok) {
      throw new Error(`Unexpected response ${response.status}`)
    }

    internalModelValue.value = false
    toast.success($t('Document accepted'))
    emit('accepted')
  } catch {
    toast.error(
      $t('An error occurred while confirming acceptance. Please try again, or reach out to support.'),
    )
  } finally {
    accepting.value = false
  }
}
</script>

<style scoped>
.accept-contract-doc-modal :deep(.wds-modal-content) {
  width: 90%;
  max-width: 600px;
}

@media (min-width: 768px) {
  .accept-contract-doc-modal :deep(.wds-modal-content) {
    width: 60%;
  }
}
</style>
